import { FunctionComponent, useEffect, useRef } from "react";
import { Marker, useMap, useApiIsLoaded } from "@vis.gl/react-google-maps";

interface MarkerWithLabelProps {
  position: { lat: number; lng: number };
  icon: string;
  name: string;
  size: number;
  onClick?: () => void;
  zIndex?: number;
}

const MarkerWithLabel: FunctionComponent<MarkerWithLabelProps> = ({
  position,
  icon,
  name,
  size,
  onClick,
  zIndex,
}) => {
  const map = useMap();
  const isApiLoaded = useApiIsLoaded();
  const overlayRef = useRef<google.maps.OverlayView | null>(null);

  useEffect(() => {
    if (!map || !isApiLoaded) {
      return;
    }

    // we must wait for `isApiLoaded` before accessing google.maps.OverlayView
    class LabelOverlay extends google.maps.OverlayView {
      private position: google.maps.LatLng;
      private text: string;
      private div: HTMLElement | null = null;

      constructor(position: google.maps.LatLng, text: string) {
        super();
        this.position = position;
        this.text = text;
      }

      onAdd() {
        this.div = document.createElement("div");
        this.div.style.position = "absolute";
        this.div.style.padding = "2px 6px";
        this.div.style.borderRadius = "4px";
        this.div.style.whiteSpace = "nowrap";
        this.div.style.fontSize = "12px";
        this.div.style.fontWeight = "bold";
        this.div.style.textShadow =
          "2px 0 0 #fff, -2px 0 0 #fff, 0 2px 0 #fff, 0 -2px 0 #fff, " +
          "2px 2px 0 #fff, -2px -2px 0 #fff, 2px -2px 0 #fff, -2px 2px 0 #fff";
        this.div.style.color = "#000";
        this.div.textContent = this.text;

        const panes = this.getPanes();
        if (panes) {
          panes.overlayMouseTarget.appendChild(this.div);
        }
      }

      draw() {
        if (!this.div) return;

        const projection = this.getProjection();
        if (!projection) return;

        const point = projection.fromLatLngToDivPixel(this.position);
        if (!point) return;

        this.div.style.left = `${point.x + 20}px`;
        this.div.style.top = `${point.y - this.div.offsetHeight / 2}px`;
      }

      onRemove() {
        if (this.div) {
          this.div.parentNode?.removeChild(this.div);
          this.div = null;
        }
      }
    }

    const latLng = new google.maps.LatLng(position.lat, position.lng);
    overlayRef.current = new LabelOverlay(latLng, name);
    overlayRef.current.setMap(map);

    return () => {
      if (overlayRef.current) {
        overlayRef.current.setMap(null);
      }
    };
  }, [map, position, name, isApiLoaded]);

  return (
    <Marker
      position={position}
      icon={{
        url: icon,
        anchor: new google.maps.Point(size / 2, size / 2),
        scaledSize: new google.maps.Size(size, size),
      }}
      zIndex={zIndex}
      title={name}
      onClick={onClick}
    />
  );
};

export { MarkerWithLabel };
